import { useState, FormEvent, ChangeEvent } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import type { CallRequestData, IntentType } from '../types';
import { initiateCall } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import { IntentSelector } from './IntentSelector';
import { DEMO_LISTINGS } from '../data/demo-listings';
import { WOJEWODZTWA } from '../data/wojewodztwa';

interface CallRequestFormProps {
  onSuccess?: () => void;
}

export function CallRequestForm({ onSuccess }: CallRequestFormProps) {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CallRequestData>({
    business_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    intent: '' as any,
    selected_listing: '',
    state: '',
    city: '',
    callConsent: false,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError(null);
  };

  const handlePhoneChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      phone: value.startsWith('+') ? value : `+${value}`,
    }));
    setError(null);
  };

  const handleIntentChange = (intent: IntentType) => {
    setFormData((prev) => ({
      ...prev,
      intent,
      selected_listing: '',
      state: '',
      city: '',
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.business_name.trim() || formData.business_name.trim().length < 2) {
      return 'Please enter your business name (at least 2 characters)';
    }
    if (!formData.first_name.trim() || formData.first_name.trim().length < 2) {
      return 'Please enter your first name (at least 2 characters)';
    }
    if (!formData.last_name.trim() || formData.last_name.trim().length < 2) {
      return 'Please enter your last name (at least 2 characters)';
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      return 'Please enter a valid email';
    }
    if (!formData.intent) {
      return 'Please select how you want to test the system';
    }
    if (formData.intent === 'buy' && !formData.selected_listing) {
      return 'Please select a property';
    }
    if (formData.intent === 'sell' && !formData.state) {
      return 'Please select a województwo';
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      return 'Please enter a valid phone number';
    }
    if (!formData.callConsent) {
      return 'You must consent to receive a demo call to continue';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await initiateCall(formData);
      
      if (result.success) {
        setIsSubmitted(true);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(result.message || 'Failed to initiate call. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setFormData({
      business_name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      intent: '' as any,
      selected_listing: '',
      state: '',
      city: '',
      callConsent: false,
    });
    setError(null);
  };

  const showListings = formData.intent === 'buy';
  const showLocation = formData.intent === 'sell';

  if (isSubmitted) {
    return (
      <div className="call-request-form success-state">
        <div className="success-badge">
          <svg className="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>{t.success.badge}</span>
        </div>
        
        <h3>{t.success.title}</h3>
        
        <p className="success-message">
          {t.success.description}
        </p>
        
        <button type="button" onClick={handleReset} className="reset-btn">
          {t.success.reset}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="call-request-form">
      <div className="form-group">
        <label htmlFor="business_name">
          {t.form.businessName.label} <span className="required">*</span>
        </label>
        <input
          type="text"
          id="business_name"
          name="business_name"
          value={formData.business_name}
          onChange={handleChange}
          placeholder={t.form.businessName.placeholder}
          autoComplete="organization"
          required
        />
        <p className="field-hint">{t.form.businessName.hint}</p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="first_name">
            {t.form.firstName.label} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            placeholder={t.form.firstName.placeholder}
            autoComplete="given-name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="last_name">
            {t.form.lastName.label} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            placeholder={t.form.lastName.placeholder}
            autoComplete="family-name"
            required
          />
        </div>
      </div>
      <p className="field-hint">{t.form.lastName.hint}</p>

      <div className="form-group">
        <label htmlFor="email">
          {t.form.email.label} <span className="required">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={t.form.email.placeholder}
          autoComplete="email"
          required
        />
        <p className="field-hint">{t.form.email.hint}</p>
      </div>

      <div className="form-group">
        <label htmlFor="intent">
          {t.form.intent.label} <span className="required">*</span>
        </label>
        <IntentSelector value={formData.intent} onChange={handleIntentChange} />
        <p className="field-hint">{t.form.intent.hint}</p>
      </div>

      {showListings && (
        <div className="form-group">
          <label htmlFor="selected_listing">
            {t.form.selectedListing.label} <span className="required">*</span>
          </label>
          <select
            id="selected_listing"
            name="selected_listing"
            value={formData.selected_listing}
            onChange={handleChange}
            className="intent-select"
            required
          >
            <option value="" disabled>
              {t.form.selectedListing.placeholder}
            </option>
            {DEMO_LISTINGS.map((listing) => (
              <option key={listing} value={listing}>
                {listing}
              </option>
            ))}
          </select>
          <p className="field-hint">{t.form.selectedListing.hint}</p>
        </div>
      )}

      {showLocation && (
        <div className="form-group">
          <p className="field-label-text">{t.form.location.label}</p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="state">
                {t.form.location.wojewodztwo.label} <span className="required">*</span>
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="intent-select"
                required
              >
                <option value="" disabled>
                  {t.form.location.wojewodztwo.placeholder}
                </option>
                {WOJEWODZTWA.map((w) => (
                  <option key={w.code} value={w.code}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="city">
                {t.form.location.city.label}{' '}
                <span className="optional-text">{t.form.location.city.optional}</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder={t.form.location.city.placeholder}
                autoComplete="address-level2"
              />
            </div>
          </div>
          <p className="field-hint">{t.form.location.hint}</p>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="phone">
          {t.form.phone.label} <span className="required">*</span>
        </label>
        <PhoneInput
          country={'us'}
          value={formData.phone}
          onChange={handlePhoneChange}
          inputProps={{
            name: 'phone',
            required: true,
            autoFocus: false,
          }}
          containerClass="phone-input-container"
          inputClass="phone-input"
          buttonClass="phone-input-button"
          enableSearch={true}
          searchPlaceholder="Search countries..."
        />
        <p className="field-hint">{t.form.phone.hint}</p>
      </div>

      <div className="form-group consent-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="callConsent"
            checked={formData.callConsent}
            onChange={handleChange}
            required
          />
          <span>
            {t.form.consent.label} <span className="required">*</span>
          </span>
        </label>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="submit-btn"
      >
        {isSubmitting ? (
          <>
            <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75"></path>
            </svg>
            {t.form.submitting}
          </>
        ) : (
          t.form.submit
        )}
      </button>

      <p className="privacy-note">
        {t.form.disclaimer}
      </p>
    </form>
  );
}
